/**
 * Lo que el autor deja hacer con su texto (M-1, 3-oct-2026).
 *
 * Rais: el perfil del autor recoge si acepta poda, si acepta que le cambien el estilo y si acepta
 * texto nuevo escrito por la IA, y lo que no se toca nunca. Se dice UNA vez, por firma, y vale para
 * todos sus libros: el motor lo aplica al plan (una pasada de poda no se hace si no quiere poda), a
 * cada pasada y a la corrección. Quien no quiere texto de la IA recibe, donde haga falta, una
 * indicación de qué escribir.
 *
 * Mientras no diga nada se acepta todo, como hasta hoy, y se dice con esas palabras.
 */
import { useState } from "react";
import { preferirAutor } from "../api";
import type { Autor } from "../api";
import { PREGUNTAS_DE_EDICION, cambiosDePreferencias, resumenDePreferencias } from "../logica";

export default function Preferencias({ autor, alGuardar }: { autor: Autor; alGuardar: () => void }) {
  const p = autor.preferencias;
  const deLaFirma = () => ({ poda: p?.poda ?? true, estilo: p?.estilo ?? true, texto_ia: p?.texto_ia ?? true, intocables: (p?.intocables ?? []).join("\n") });
  const [abierto, setAbierto] = useState(false);
  const [v, setV] = useState(deLaFirma);
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  if (!p) return null;

  const cambios = cambiosDePreferencias(p, v);
  async function guardar() {
    if (!cambios) { setAbierto(false); return; }
    setOcupado(true); setFallo(null);
    try { await preferirAutor(autor.id, cambios); setAbierto(false); alGuardar(); }
    catch (e) { setFallo((e as Error).message); }
    finally { setOcupado(false); }
  }

  if (!abierto) {
    return (
      <p className="vacio">
        <b>Lo que dejas hacer con tu texto:</b> {resumenDePreferencias(p)}{" "}
        <button className="btn texto" onClick={() => { setV(deLaFirma()); setFallo(null); setAbierto(true); }}>{p.declaradas ? "Cambiarlo" : "Decirlo"}</button>
      </p>
    );
  }
  return (
    <div className="formulario" style={{ maxWidth: "none" }}>
      <p className="vacio"><b>Lo que dejas hacer con tu texto.</b> Vale para todos los libros de esta firma, y se aplica al plan, a cada pasada y a la corrección.</p>
      <div className="lista-etapa">
        {PREGUNTAS_DE_EDICION.map((q) => (
          <label className="fila-etapa casilla" key={q.id}>
            <input type="checkbox" checked={v[q.id]} disabled={ocupado} aria-label={q.titulo}
                   onChange={() => setV((x) => ({ ...x, [q.id]: !x[q.id] }))} />
            <div className="que"><b>{q.titulo}</b><span className="vacio">{v[q.id] ? q.si : q.no}</span></div>
            <span className="chip-estado">{v[q.id] ? "sí" : "no"}</span>
          </label>
        ))}
      </div>
      <label className="campo">
        <span>Lo que no se toca nunca <i>(una cosa por línea; en cualquier libro de esta firma)</i></span>
        <div className="dir">
          <textarea className="escribir" rows={3} style={{ resize: "vertical" }} value={v.intocables} disabled={ocupado}
                    onChange={(e) => setV((x) => ({ ...x, intocables: e.target.value }))}
                    placeholder={"Por ejemplo:\nlos epígrafes\nlos diálogos en dialecto"} />
        </div>
      </label>
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
      <div className="botones">
        <button className="btn" disabled={ocupado || !cambios} onClick={guardar}>{ocupado ? "Guardando…" : "Guardar"}</button>
        <button className="btn texto" disabled={ocupado} onClick={() => setAbierto(false)}>Cancelar</button>
      </div>
    </div>
  );
}
